<template>
    <div ref="hcaptchaElementRef" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHcaptchaReady } from '~/composables/create-hcaptcha'

const hcaptchaElementRef = ref<HTMLElement | null>(null)
const widgetId = ref<number | null>(null)
const hcaptchaReady = useHcaptchaReady()

// Passive verification should finish quickly, while an interactive challenge
// needs enough time for accessibility without leaving LoginModal pending forever.
const HCAPTCHA_EXECUTION_TIMEOUT_MS = 30_000
const HCAPTCHA_INTERACTIVE_TIMEOUT_MS = 120_000

const props = defineProps<{
    // This public key comes from the org resolver so support can switch an org
    // between passive and always-challenge hCaptcha keys without redeploying CPX.
    siteKey: string
}>()

let pendingResolve: ((token: string) => void) | undefined
let pendingReject: ((error: Error) => void) | undefined
let pendingTimeout: ReturnType<typeof setTimeout> | undefined

const emit = defineEmits<{
    (e: 'token', payload: string): void
    (e: 'challengeOpened'): void
}>()

onMounted(renderHcaptcha)
onBeforeUnmount(() => {
    rejectPending(new Error('hcaptcha-unmounted'))
})

watch(hcaptchaReady, renderHcaptcha)
watch(() => props.siteKey, renderHcaptcha)

function renderHcaptcha() {
    if (
        !hcaptchaReady.value ||
        widgetId.value !== null ||
        props.siteKey.trim() === '' ||
        hcaptchaElementRef.value === null ||
        window.hcaptcha === undefined
    ) {
        return
    }

    // Render invisibly so clicking "Send Verification Code" starts the
    // challenge only when hCaptcha decides one is needed for this site key.
    widgetId.value = window.hcaptcha.render(hcaptchaElementRef.value, {
        sitekey: props.siteKey,
        size: 'invisible',
        callback: (token: string) => {
            emit('token', token)
            resolvePending(token)
        },
        'expired-callback': () => {
            emit('token', '')
            rejectPending(new Error('hcaptcha-expired'))
        },
        'error-callback': () => {
            emit('token', '')
            rejectPending(new Error('hcaptcha-error'))
        },
        'open-callback': () => {
            // This is telemetry only; the server still trusts only the verified
            // hCaptcha token and never this client-reported challenge state.
            emit('challengeOpened')
            schedulePendingTimeout(HCAPTCHA_INTERACTIVE_TIMEOUT_MS)
        },
        'close-callback': () => {
            // If the user dismisses an interactive challenge, unblock the
            // LoginModal so they can retry instead of waiting forever.
            emit('token', '')
            rejectPending(new Error('hcaptcha-closed'))
        },
        'chalexpired-callback': () => {
            // This timeout is for an open challenge UI, distinct from the token
            // expiry callback that can fire after a successful solve.
            emit('token', '')
            rejectPending(new Error('hcaptcha-challenge-expired'))
        },
        theme: 'light'
    })
}

function execute(): Promise<string> {
    renderHcaptcha()

    if (widgetId.value === null || window.hcaptcha === undefined) {
        return Promise.reject(new Error('hcaptcha-not-ready'))
    }

    return new Promise((resolve, reject) => {
        rejectPending(new Error('hcaptcha-restarted'))
        pendingResolve = resolve
        pendingReject = reject
        schedulePendingTimeout(HCAPTCHA_EXECUTION_TIMEOUT_MS)

        try {
            window.hcaptcha?.execute(widgetId.value as number)
        } catch (error) {
            // Synchronous SDK failures must also clear the pending timeout and
            // restore LoginModal instead of leaving stale execution state.
            rejectPending(
                error instanceof Error ? error : new Error('hcaptcha-error')
            )
        }
    })
}

function reset() {
    // hCaptcha tokens are short-lived and single-use, so reset after failed
    // server attempts before allowing another send-code submission.
    if (widgetId.value !== null && window.hcaptcha !== undefined) {
        window.hcaptcha.reset(widgetId.value)
        emit('token', '')
    }
}

function resolvePending(token: string) {
    if (pendingResolve !== undefined) {
        pendingResolve(token)
    }
    clearPending()
}

function rejectPending(error: Error) {
    if (pendingReject !== undefined) {
        pendingReject(error)
    }
    clearPending()
}

function clearPending() {
    if (pendingTimeout !== undefined) {
        clearTimeout(pendingTimeout)
    }

    pendingResolve = undefined
    pendingReject = undefined
    pendingTimeout = undefined
}

function schedulePendingTimeout(timeoutMs: number) {
    if (pendingTimeout !== undefined) {
        clearTimeout(pendingTimeout)
    }

    // This catches SDK or network failures where hCaptcha invokes neither a
    // success callback nor one of its documented failure callbacks.
    pendingTimeout = setTimeout(() => {
        emit('token', '')
        rejectPending(new Error('hcaptcha-timeout'))
    }, timeoutMs)
}

defineExpose({ execute, reset })
</script>
